import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Award, Download, FileText, ShieldCheck, Loader2, Camera } from "lucide-react";
import { api } from "../lib/api";

const ROLES = ["Filmmaker", "Director", "Producer", "Press", "Jury"];

export default function FestivalCredentialCard({ user }) {
  const [cred, setCred] = useState(null);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [role, setRole] = useState("Filmmaker");
  const fileRef = useRef(null);

  const eligible = !!(user?.is_filmmaker || user?.is_admin || (user?.roles || []).some((r) => r === "creator" || r === "filmmaker"));

  useEffect(() => {
    if (!user?.user_id) return;
    setLoading(true);
    api.get("/festival/credentials/me")
      .then((r) => setCred(r.data?.credential || null))
      .catch(() => setCred(null))
      .finally(() => setLoading(false));
  }, [user]);

  const issue = async () => {
    setIssuing(true);
    try {
      const { data } = await api.post("/festival/credentials", { role });
      setCred(data.credential);
      toast.success(data.issued ? "Festival credential issued 🎬" : "Credential ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not issue credential");
    } finally {
      setIssuing(false);
    }
  };

  const downloadCard = async (ext) => {
    if (!cred) return;
    try {
      const res = await api.get(`/festival/credentials/${cred.credential_id}/card.${ext}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${cred.credential_id}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${ext.toUpperCase()} downloaded`);
    } catch {
      toast.error(`Could not download ${ext.toUpperCase()}`);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file || !cred) return;
    if (!file.type.startsWith("image/")) { toast.error("Image file required"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Photo too large (max 5MB)"); return; }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "credential_photo");
      const { data } = await api.post("/festival/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const patched = await api.patch(`/festival/credentials/${cred.credential_id}`, { photo_url: data.url });
      setCred(patched.data.credential);
      toast.success("Photo updated — re-download to apply");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed");
    } finally { setPhotoUploading(false); }
  };

  if (loading) {
    return (
      <div data-testid="credential-widget" className="bg-[#141414] border border-white/5 rounded-lg p-6 flex items-center gap-3 text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading credential…
      </div>
    );
  }

  if (!eligible) {
    return (
      <div data-testid="credential-widget" className="bg-[#141414] border border-white/5 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-2">
          <Award className="w-4 h-4 text-amber-300" />
          <h3 className="font-semibold">World Film Festival Credential</h3>
        </div>
        <p className="text-sm text-gray-400">
          Festival credentials are issued to verified filmmakers and content owners. Apply via the <a href="/creator/apply" className="text-amber-300 hover:underline">Creator Application</a> first.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="credential-widget" className="bg-gradient-to-br from-[#1a0608] via-[#100406] to-[#0A0A0A] border border-amber-300/15 rounded-lg p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Award className="w-5 h-5 text-amber-300" />
          <h3 className="font-bold text-lg">World Film Festival Credential</h3>
        </div>
        {cred && (
          <span data-testid="credential-status-pill" className={`text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full ${
            cred.status === "active" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
            : "bg-red-500/15 text-red-300 border border-red-500/30"
          }`}>
            {cred.status === "active" ? "Active" : "Revoked"}
          </span>
        )}
      </div>

      {!cred ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-400">
            Issue your one-of-a-kind festival ID card. Each card is HMAC-signed — duplicates fail verification at the venue.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              data-testid="credential-role-select"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="input-dark flex-1"
            >
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button
              data-testid="credential-issue-btn"
              onClick={issue}
              disabled={issuing}
              className="btn-red px-6 py-3 rounded font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {issuing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {issuing ? "Issuing…" : "Issue my credential"}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="relative shrink-0">
              {cred.photo_url ? (
                <img
                  src={cred.photo_url.startsWith("http") ? cred.photo_url : `${process.env.REACT_APP_BACKEND_URL}${cred.photo_url}`}
                  alt={cred.name}
                  className="w-20 h-20 rounded-full object-cover border-2 border-amber-300/50"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-[#1a0608] border-2 border-amber-300/50 flex items-center justify-center text-3xl font-black text-[#E50914]">
                  {(cred.name || "?").trim().charAt(0).toUpperCase()}
                </div>
              )}
              <button
                data-testid="cred-photo-upload-btn"
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={photoUploading}
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-amber-300 hover:bg-amber-200 text-black flex items-center justify-center shadow-lg disabled:opacity-60"
                title={cred.photo_url ? "Change photo" : "Upload photo"}
              >
                {photoUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadPhoto(e.target.files?.[0])}
              />
            </div>
            <div className="flex-1 min-w-0 grid grid-cols-2 gap-3 text-sm">
              <Field label="Name" value={cred.name} />
              <Field label="Role" value={cred.role} testid="cred-role" />
              <Field label="Credential ID" value={cred.credential_id} mono testid="cred-id" />
              <Field label="Issued" value={cred.issued_at_display} />
              <Field label="Edition" value={`WFF · ${cred.year}`} />
              <Field label="Valid until" value={`Dec 31, ${cred.year}`} />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button data-testid="cred-download-png" onClick={() => downloadCard("png")} className="btn-glass flex-1 py-3 rounded font-semibold flex items-center justify-center gap-2">
              <Download className="w-4 h-4" /> Download PNG
            </button>
            <button data-testid="cred-download-pdf" onClick={() => downloadCard("pdf")} className="btn-red flex-1 py-3 rounded font-semibold flex items-center justify-center gap-2">
              <FileText className="w-4 h-4" /> Printable PDF
            </button>
          </div>
          <p className="text-[11px] text-gray-500 pt-2 border-t border-white/5">
            Tip: Print the PDF, punch a hole at the top, clip to a lanyard. The QR code on the front lets festival staff verify authenticity instantly at the venue.
          </p>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-0.5">{label}</div>
      <div data-testid={testid} className={`text-gray-200 ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</div>
    </div>
  );
}
